
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { format, subMonths, addMonths, startOfMonth, endOfMonth } from "date-fns";
import { fetchPayouts } from "@/utils/apiUtils";

interface EarningsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const EarningsDialog = ({ open, onOpenChange }: EarningsDialogProps) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  
  const startDate = format(startOfMonth(currentMonth), "yyyy-MM-dd");
  const endDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");
  
  const { data: payoutsData, isLoading } = useQuery({
    queryKey: ["payouts", startDate, endDate],
    queryFn: () => fetchPayouts(startDate, endDate),
    enabled: open // Only fetch when dialog is open
  });
  
  // Calculate total earnings from payouts data
  const totalEarnings = payoutsData?.results?.reduce((total: number, payout: any) => {
    return total + (parseFloat(payout.amount) || 0);
  }, 0) || 0;
  
  // Process data for chart
  const chartData = payoutsData?.results?.map((payout: any) => {
    const date = new Date(payout.created_at);
    return {
      name: format(date, "MMM d"),
      amount: parseFloat(payout.amount) || 0,
    };
  }) || [];
  
  const handlePreviousMonth = () => {
    setCurrentMonth(prevMonth => subMonths(prevMonth, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(prevMonth => addMonths(prevMonth, 1));
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] p-0 rounded-lg bg-white">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">My Earnings</h2>
            <button 
              onClick={() => onOpenChange(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              <button 
                onClick={handlePreviousMonth}
                className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                <ChevronLeft size={20} className="text-gray-700" />
              </button>
              <button 
                onClick={handleNextMonth}
                className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                <ChevronRight size={20} className="text-gray-700" />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-900">
              {format(currentMonth, "MMMM yyyy")}
            </h3>
          </div>
          
          <div className="border rounded-lg p-6">
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-600 mb-1">Total Earnings</h4>
              <div className="text-3xl font-bold text-gray-900">{formatCurrency(totalEarnings)}</div>
            </div>
            
            {isLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <p className="text-gray-600">Loading earnings data...</p>
              </div>
            ) : chartData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center">
                <p className="text-gray-600">No earnings data available for this period</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: '#555', fontSize: 12 }} 
                    tickLine={false}
                    axisLine={{ stroke: '#eee' }}
                  />
                  <YAxis 
                    tick={{ fill: '#555', fontSize: 12 }} 
                    tickLine={false} 
                    axisLine={false}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => [`${formatCurrency(value as number)}`, 'Earnings']}
                    contentStyle={{ background: 'white', border: '1px solid #eee', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                    labelStyle={{ color: '#333', fontWeight: 'bold', marginBottom: '4px' }}
                  />
                  <Bar 
                    dataKey="amount" 
                    fill="#4ade80" 
                    radius={[4, 4, 0, 0]} 
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          
          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Recent Payments</h3>
            {isLoading ? (
              <div className="text-center py-4">
                <p className="text-gray-600">Loading payment history...</p>
              </div>
            ) : !payoutsData?.results || payoutsData.results.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-600">No payment history available</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payoutsData.results.map((payout: any, index: number) => (
                      <tr key={payout.id || index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {format(new Date(payout.created_at), "MMM d, yyyy")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {payout.description || "Trip payment"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          {formatCurrency(parseFloat(payout.amount) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EarningsDialog;
